import React from 'react';
import { Document, Page, View, Text } from '@react-pdf/renderer';
import { commonStyles, COLORS, formatExamDate, historyToString, calculateVolume, formatListItems, buildCombinedReason, getFacilityDisplayName } from '../utils/pdfHelpers';
import { PdfPageHeader, PdfPatientBox, PdfCallReport, PdfAccentSectionHeader, PdfSectionHeader, PdfLabelValue, PdfMeasurementBox, PdfCommentsBox, PdfFooter } from '../utils/PdfSharedComponents';

export function SoftTissuePDF({ data, facilityInfo }) {
  const reason = buildCombinedReason(data.reason, data.history);

  const hernia = data.hernia || {};
  const mass = data.mass || {};
  const other = data.other || {};

  const hernias = hernia.hernias || [];
  const masses = mass.masses || [];

  const findingsLines = [];
  if (hernia.evaluated) {
    if (hernias.length > 0) {
      hernias.forEach((h, i) => {
        if (h.findings === 'No hernia visualized') {
          findingsLines.push(`Hernia ${i + 1}: ${h.location ? h.location + ' — ' : ''}${h.findings}`);
          return;
        }
        const contents = [
          h.containsFat && 'fat',
          h.containsBowel && 'bowel',
          h.containsFluid && 'fluid',
        ].filter(Boolean).join(', ');
        findingsLines.push(
          `Hernia ${i + 1}: ${h.location || ''}${h.findings ? ' — ' + h.findings : ''}${contents ? ' (contains: ' + contents + ')' : ''}${h.maximumSize ? ', size: ' + h.maximumSize + ' cm' : ''}`
        );
      });
    } else {
      findingsLines.push('Hernia: No hernia identified');
    }
  }
  if (mass.evaluated) {
    if (masses.length > 0) {
      masses.forEach((m, i) => {
        findingsLines.push(
          `Mass ${i + 1}: ${m.location || ''}${m.composition ? ', ' + m.composition : ''}${(m.size1 && m.size2 && m.size3) ? `, ${m.size1} x ${m.size2} x ${m.size3} cm` : ''}`
        );
      });
    } else {
      findingsLines.push('Mass: No mass identified');
    }
  }
  if (other.evaluated && other.findings) {
    findingsLines.push(`Other: ${other.findings}`);
  }

  return (
    <Document>
      <Page size="LETTER" style={commonStyles.page}>
        <PdfPageHeader facilityInfo={facilityInfo} examTitle="SOFT TISSUE ULTRASOUND WORKSHEET" />
        <PdfPatientBox
          firstName={data.firstName}
          lastName={data.lastName}
          mrn={data.mrn}
          examDate={data.examDate}
          technologist={data.technologist}
          studentWorksheet={data.studentWorksheet}
          supervisingSonographer={data.supervisingSonographer}
          reason={reason}
        />
        <PdfCallReport show={data.callReport} phoneNumber={data.phoneNumber} />

        <View style={commonStyles.twoCol}>
          <View style={commonStyles.colLeft}>
            <View style={commonStyles.measurementBox}>
              <Text style={[commonStyles.infoBoxText, { fontFamily: 'Helvetica-Bold', marginBottom: 4 }]}>FINDINGS SUMMARY</Text>
              <Text style={commonStyles.infoBoxText}>{findingsLines.join('\n') || 'No findings recorded'}</Text>
            </View>
            <PdfCommentsBox comments={data.comments} />
          </View>

          <View style={commonStyles.colRight}>
            {hernia.evaluated && (
              <>
                <PdfAccentSectionHeader title="HERNIA EVALUATION" />
                {hernias.length === 0 && <PdfLabelValue label="Finding:" value="No hernia identified" forceBlack />}
                {hernias.map((h, i) => (
                  h.findings === 'No hernia visualized' ? (
                    <View key={i} style={{ marginBottom: 6 }}>
                      {h.location && <PdfLabelValue label="Location:" value={h.location} forceBlack />}
                      <PdfLabelValue label="Findings:" value={h.findings} forceBlack />
                    </View>
                  ) : (
                    <View key={i} style={{ marginBottom: 6 }}>
                      <Text style={[commonStyles.label, { marginBottom: 2 }]}>Hernia {i + 1}:</Text>
                      {h.location && <PdfLabelValue label="Location:" value={h.location} forceBlack />}
                      {h.findings && <PdfLabelValue label="Findings:" value={h.findings} isAbnormal />}
                      {h.maximumSize && <PdfLabelValue label="Maximum size:" value={`${h.maximumSize} cm`} isAbnormal />}
                      <PdfLabelValue label="Contains fat:" value={h.containsFat ? 'Yes' : 'No'} isAbnormal={h.containsFat} />
                      <PdfLabelValue label="Contains bowel:" value={h.containsBowel ? 'Yes' : 'No'} isAbnormal={h.containsBowel} />
                      <PdfLabelValue label="Contains fluid:" value={h.containsFluid ? 'Yes' : 'No'} isAbnormal={h.containsFluid} />
                    </View>
                  )
                ))}
              </>
            )}

            {mass.evaluated && (
              <>
                <PdfAccentSectionHeader title="MASS EVALUATION" />
                {masses.length === 0 && <PdfLabelValue label="Finding:" value="No mass identified" forceBlack />}
                {masses.map((m, i) => (
                  <View key={i} style={{ marginBottom: 6 }}>
                    <Text style={[commonStyles.label, { marginBottom: 2 }]}>Mass {i + 1}:</Text>
                    {m.location && <PdfLabelValue label="Location:" value={m.location} forceBlack />}
                    {m.composition && <PdfLabelValue label="Composition:" value={m.composition} isAbnormal />}
                    {(m.size1 && m.size2 && m.size3) && (
                      <PdfLabelValue label="Size:" value={`${m.size1} x ${m.size2} x ${m.size3} cm`} isAbnormal />
                    )}
                  </View>
                ))}
              </>
            )}

            {other.evaluated && (
              <>
                <PdfAccentSectionHeader title="OTHER FINDINGS" />
                {other.findings
                  ? <PdfLabelValue label="Findings:" value={other.findings} isAbnormal />
                  : <PdfLabelValue label="Finding:" value="None" forceBlack />
                }
              </>
            )}
          </View>
        </View>

        <PdfFooter facilityInfo={facilityInfo} />
      </Page>
    </Document>
  );
}
